import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { AuthProvider } from "@/contexts/auth";
import { LanguageProvider } from "@/contexts/language";
import ProtectedRoute from "@/components/ProtectedRoute";
import { supabase } from "@/integrations/supabase/client";
import Index from "./pages/Index";
import Generate from "./pages/Generate";
import Entries from "./pages/Entries";
import Premium from "./pages/Premium";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import SharedEntry from "./pages/SharedEntry";
import Statistics from "./pages/Statistics";
import Gallery from "./pages/Gallery";
import Admin from "./pages/Admin";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

export const checkPremiumStatus = async (userId: string | undefined) => {
  if (!userId) return false;
  
  try {
    console.log("Checking premium status for user:", userId);
    const { data, error } = await supabase
      .from('profiles')
      .select('is_premium')
      .eq('id', userId)
      .single();
      
    if (error) {
      console.error('Error checking premium status:', error);
      return false;
    }
    
    const isPremium = data?.is_premium || false;
    console.log("Premium status result from database:", isPremium);
    localStorage.setItem('premiumSubscription', isPremium ? 'true' : 'false');
    return isPremium;
  } catch (error) {
    console.error('Error checking premium status:', error);
    return false;
  }
};

export const checkPremiumStatusSync = () => {
  return localStorage.getItem('premiumSubscription') === 'true';
};

export const isStatisticsAvailable = () => {
  return true; // Statistics are now available to all users
};

export const isGalleryAvailable = () => {
  return checkPremiumStatusSync();
};

const App = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const checkUserSubscription = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user) {
          await checkPremiumStatus(data.session.user.id);
        }
      } catch (error) {
        console.error("Error checking user session:", error);
      } finally {
        setIsInitialized(true);
      }
    };
    
    checkUserSubscription();
  }, []);

  if (!isInitialized) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <LanguageProvider>
            <BrowserRouter>
              <Toaster />
              <Sonner />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route 
                  path="/generate" 
                  element={
                    <ProtectedRoute public={true}>
                      <Generate />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/entries" 
                  element={
                    <ProtectedRoute public={true}>
                      <Entries />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/statistics" 
                  element={
                    <ProtectedRoute public={true}>
                      <Statistics />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/gallery" 
                  element={
                    <ProtectedRoute public={true}>
                      <Gallery />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/premium" element={<Premium />} />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/entries/shared/:entryId" element={<SharedEntry />} />
                <Route path="/index.html" element={<Navigate to="/" replace />} />
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </LanguageProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
